<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="text-xl font-semibold text-slate-900">企营增开票</h1>
            <p class="mt-1 text-sm text-slate-500">维护月度客户名单、区域排列、供应链链路和校验结果。</p>
          </div>
          <div class="list-page-heading-actions">
            <button
              type="button"
              class="btn-secondary inline-flex items-center gap-2"
              :disabled="!selectedPlanId || state.loading.value"
              @click="reloadCurrentPlan"
            >
              <ArrowPathIcon class="h-4 w-4" />
              刷新
            </button>
            <button
              type="button"
              class="btn-primary inline-flex items-center gap-2"
              v-permission="'enterprise-invoice:plan:confirm'"
              :disabled="!selectedPlanId || state.syncing.value || state.hasErrorIssues.value"
              @click="confirmPlan"
            >
              <CheckCircleIcon class="h-4 w-4" />
              确认方案
            </button>
          </div>
        </div>
      </div>
    </template>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)] 2xl:grid-cols-[320px_minmax(0,1fr)]">
      <InvoicePlanPanel
        ref="planPanelRef"
        v-model="selectedPlanId"
        @create="openCreatePlan"
        @copy="openCopyPlan"
      />

      <section class="min-h-[520px] min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white lg:min-h-[640px]">
        <div v-if="!selectedPlanId" class="flex h-full min-h-[420px] items-center justify-center p-6 sm:p-10 lg:min-h-[640px]">
          <div class="text-center">
            <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <CheckCircleIcon class="h-6 w-6" />
            </div>
            <p class="mt-3 text-sm font-medium text-slate-700">请选择或新建一个月度方案</p>
          </div>
        </div>

        <div v-else class="flex h-full min-h-[520px] flex-col lg:min-h-[640px]">
          <header class="border-b border-slate-200 px-4 py-4 sm:px-5">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <h2 class="truncate text-lg font-semibold text-slate-900">{{ state.plan.value?.planName || '月度方案' }}</h2>
                  <span :class="['status-badge', statusClass(state.plan.value?.status)]">
                    {{ statusLabel(state.plan.value?.status) }}
                  </span>
                </div>
                <p class="mt-1 text-sm text-slate-500">
                  {{ state.plan.value?.planMonth || '-' }} · 金额按 100 元取整
                </p>
              </div>
              <div class="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                <!-- <button type="button" class="btn-secondary" :disabled="state.syncing.value" @click="validatePlan">校验方案</button> -->
                <button type="button" class="btn-secondary whitespace-nowrap" v-permission="'enterprise-invoice:list:confirm'" :disabled="state.syncing.value" @click="confirmList">确认名单</button>
                <button type="button" class="btn-primary whitespace-nowrap" v-permission="'enterprise-invoice:chain:generate'" :disabled="state.syncing.value || !canGenerateChains(state.plan.value)" @click="generateChains">生成链路</button>
              </div>
            </div>
          </header>

          <div class="overflow-x-auto px-4 sm:px-5 border-b border-slate-200">
            <nav class="flex min-w-max gap-2" aria-label="企营增开票视图">
              <button
                type="button"
                :class="tabClass('monthly-customers')"
                @click="activeTab = 'monthly-customers'"
              >
                月度客户
              </button>
              <button
                type="button"
                :class="tabClass('list')"
                @click="activeTab = 'list'"
              >
                名单
              </button>
              <button
                type="button"
                :class="tabClass('chains')"
                @click="activeTab = 'chains'"
              >
                链路
              </button>
            </nav>
          </div>

          <main class="min-h-0 flex-1 overflow-y-auto p-3 sm:p-5">
            <div v-if="state.loading.value" class="flex h-80 items-center justify-center text-sm text-slate-500">
              加载中...
            </div>

            <template v-else>
              <section v-show="activeTab === 'monthly-customers'" class="space-y-4">
                <InvoiceMonthlyCustomerWorkbench
                  v-model:keyword="customerWorkbenchKeyword"
                  v-model:joined-filter="customerWorkbenchJoinedFilter"
                  v-model:planner-user-id="customerWorkbenchPlannerUserId"
                  v-model:finance-user-id="customerWorkbenchFinanceUserId"
                  v-model:channel-source="customerWorkbenchChannelSource"
                  :rows="customerWorkbenchRows"
                  :loading="customerWorkbenchLoading"
                  :syncing="state.syncing.value"
                  :page="customerWorkbenchPage"
                  :size="customerWorkbenchSize"
                  :total="customerWorkbenchTotal"
                  :customer-tags="customerTags"
                  :downstream-allocations="state.downstreamAllocations.value"
                  :downstream-tag-preferences="state.downstreamTagPreferences.value"
                  @search="searchCustomerWorkbench"
                  @page-change="changeCustomerWorkbenchPage"
                  @size-change="changeCustomerWorkbenchSize"
                  @create-master="openCreateMasterCustomer"
                  @edit-master="openEditMasterCustomer"
                  @delete-master="deleteMasterCustomer"
                  @join-month="openJoinMonth"
                  @edit-month="openEditMonth"
                  @remove-month="removeFromMonth"
                />
              </section>

              <section v-show="activeTab === 'list'" class="space-y-4">
                <InvoiceRegionListEditor
                  :plan="state.plan.value"
                  :regions="state.regions.value"
                  :customers="state.customers.value"
                  :customers-by-region="state.customersByRegion.value"
                  :downstream-allocations="state.downstreamAllocations.value"
                  :downstream-tag-preferences="state.downstreamTagPreferences.value"
                  :syncing="state.syncing.value"
                  @create-region="openCreateRegion"
                  @rename-region="openRenameRegion"
                  @delete-region="deleteRegion"
                  @reorder-regions="reorderRegions"
                  @sort-regions-by-amount="sortRegionsByAmount"
                  @create-customer="openCreateMonthFromRegion"
                  @edit-customer="openEditMonthFromPlanCustomer"
                  @move-customer="moveMatrixCustomer"
                  @delete-customer="deleteCustomer"
                  @confirm-list="confirmList"
                />
              </section>

              <section v-show="activeTab === 'chains'" class="space-y-4">
                <InvoiceChainWorkspace
                  :plan="state.plan.value"
                  :chains="state.chains.value"
                  :customers="state.customers.value"
                  :regions="state.regions.value"
                  :validation-issues="state.validationIssues.value"
                  :syncing="state.syncing.value"
                  :exporting="exportingChains"
                  :focus-chain-id="companyRelationFocusChainId"
                  v-model:company-relation-keyword="companyRelationKeyword"
                  :company-relation-result="companyRelationResult"
                  :company-relation-loading="companyRelationLoading"
                  @generate="generateChains"
                  @export-chains="exportChains"
                  @regenerate-unlocked="regenerateUnlocked"
                  @generate-unfilled="generateUnfilled"
                  @edit-node="openEditNode"
                  @lock-chain="lockChain"
                  @lock-node="lockNode"
                  @regenerate-chain="regenerateChain"
                  @regenerate-after-node="regenerateAfterNode"
                  @search-company-relation="loadCompanyChainRelations"
                  @clear-company-relation="clearCompanyChainRelations"
                  @locate-company-relation="locateCompanyRelation"
                />
              </section>
            </template>
          </main>
        </div>
      </section>
    </div>

    <InvoicePlanModal
      v-model="planModal.visible"
      :mode="planModal.mode"
      :base-plan="planModal.basePlan"
      @saved="afterPlanSaved"
    />
    <InvoiceCustomerModal
      v-model="customerModal.visible"
      :plan-id="selectedPlanId"
      :customer="customerModal.customer"
      :regions="state.regions.value"
      :downstream-allocations="state.downstreamAllocations.value"
      :downstream-tag-preferences="state.downstreamTagPreferences.value"
      @saved="handleCustomerModalSaved"
      @configure-downstream="openDownstreamAllocation"
      @configure-tag-preference="openDownstreamTagPreference"
    />
    <InvoiceCustomerMasterModal
      v-model="masterCustomerModal.visible"
      :mode="masterCustomerModal.mode"
      :customer="masterCustomerModal.customer"
      :available-tags="customerTags"
      :selected-tag-ids="masterCustomerModal.tagIds"
      :tags-loading="masterCustomerModal.tagsLoading"
      :regions="state.regions.value"
      :customers="state.customers.value"
      :downstream-allocations="state.downstreamAllocations.value"
      :downstream-tag-preferences="state.downstreamTagPreferences.value"
      :submitting="masterCustomerModal.saving"
      @submit="saveMasterCustomer"
    />
    <InvoiceRegionModal
      v-model="regionModal.visible"
      :region="regionModal.region"
      @submit="saveRegionModal"
    />
    <InvoiceNodeEditPanel
      v-model="nodePanel.visible"
      :context="nodePanel.context"
      @saved="saveNodeEdit"
    />
    <InvoiceDownstreamAllocationDialog
      v-model="downstreamAllocationDialog.visible"
      :customer="downstreamAllocationDialog.customer"
      :customers="state.customers.value"
      :allocations="state.downstreamAllocations.value"
      :submitting="state.syncing.value"
      @submit="saveDownstreamAllocations"
    />
    <InvoiceDownstreamTagPreferenceDialog
      v-model="downstreamTagPreferenceDialog.visible"
      :customer="downstreamTagPreferenceDialog.customer"
      :tags="customerTags"
      :preferences="state.downstreamTagPreferences.value"
      :submitting="state.syncing.value"
      @submit="saveDownstreamTagPreferences"
    />
  </ListPageShell>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/vue/24/outline'
import ListPageShell from '@/components/ListPageShell.vue'
import {
  confirmEnterpriseInvoiceList,
  confirmEnterpriseInvoicePlan,
  createEnterpriseInvoiceCustomer,
  createEnterpriseInvoiceCustomerWithMonth,
  createEnterpriseInvoiceRegion,
  deleteEnterpriseInvoiceCustomer,
  deleteEnterpriseInvoicePlanCustomer,
  deleteEnterpriseInvoiceRegion,
  exportEnterpriseInvoiceChains,
  generateEnterpriseInvoiceChains,
  generateEnterpriseInvoiceForUnfilledCustomers,
  getEnterpriseInvoiceCompanyChainRelations,
  getEnterpriseInvoiceCustomerTagOptions,
  getEnterpriseInvoiceCustomerTags,
  getEnterpriseInvoiceCustomerWorkbench,
  lockEnterpriseInvoiceChain,
  lockEnterpriseInvoiceChainNode,
  moveEnterpriseInvoiceCustomers,
  replaceEnterpriseInvoiceCustomerTags,
  replaceEnterpriseInvoiceDownstreamAllocations,
  replaceEnterpriseInvoiceDownstreamTagPreferences,
  regenerateEnterpriseInvoiceAfterNode,
  regenerateEnterpriseInvoiceChain,
  regenerateEnterpriseInvoiceUnlockedChains,
  reorderEnterpriseInvoiceRegions,
  sortEnterpriseInvoiceRegionsByAmount,
  unlockEnterpriseInvoiceChain,
  updateEnterpriseInvoiceCustomer,
  updateEnterpriseInvoiceChainNode,
  updateEnterpriseInvoiceRegion,
} from '@/api/invoice'
import { useEnterpriseInvoicePlanState } from './composables/useEnterpriseInvoicePlanState'
import { canGenerateChains, statusLabel, statusTone } from './utils'
import InvoicePlanPanel from './components/InvoicePlanPanel.vue'
import InvoicePlanModal from './components/InvoicePlanModal.vue'
import InvoiceCustomerModal from './components/InvoiceCustomerModal.vue'
import InvoiceCustomerMasterModal from './components/InvoiceCustomerMasterModal.vue'
import InvoiceRegionModal from './components/InvoiceRegionModal.vue'
import InvoiceMonthlyCustomerWorkbench from './components/InvoiceMonthlyCustomerWorkbench.vue'
import InvoiceRegionListEditor from './components/InvoiceRegionListEditor.vue'
import InvoiceChainWorkspace from './components/InvoiceChainWorkspace.vue'
import InvoiceNodeEditPanel from './components/InvoiceNodeEditPanel.vue'
import InvoiceDownstreamAllocationDialog from './components/InvoiceDownstreamAllocationDialog.vue'
import InvoiceDownstreamTagPreferenceDialog from './components/InvoiceDownstreamTagPreferenceDialog.vue'

const selectedPlanId = ref(null)
const activeTab = ref('monthly-customers')
const planPanelRef = ref(null)
const state = useEnterpriseInvoicePlanState()
const customerWorkbenchRows = ref([])
const customerWorkbenchLoading = ref(false)
const customerWorkbenchKeyword = ref('')
const customerWorkbenchJoinedFilter = ref('all')
const customerWorkbenchPlannerUserId = ref(null)
const customerWorkbenchFinanceUserId = ref(null)
const customerWorkbenchChannelSource = ref('')
const customerWorkbenchPage = ref(1)
const customerWorkbenchSize = ref(20)
const customerWorkbenchTotal = ref(0)
const customerTags = ref([])
const exportingChains = ref(false)
const companyRelationKeyword = ref('')
const companyRelationResult = ref(null)
const companyRelationLoading = ref(false)
const companyRelationFocusChainId = ref(null)

const planModal = reactive({ visible: false, mode: 'create', basePlan: null })
const customerModal = reactive({ visible: false, customer: null })
const masterCustomerModal = reactive({
  visible: false,
  mode: 'create',
  customer: null,
  tagIds: [],
  tagsLoading: false,
  saving: false
})
const regionModal = reactive({ visible: false, region: null })
const nodePanel = reactive({ visible: false, context: null })
const downstreamAllocationDialog = reactive({ visible: false, customer: null })
const downstreamTagPreferenceDialog = reactive({ visible: false, customer: null })

const badgeClassByTone = {
  neutral: 'bg-slate-100 text-slate-600',
  primary: 'bg-blue-100 text-blue-700',
  amber: 'bg-amber-100 text-amber-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  red: 'bg-red-100 text-red-700'
}

const tabBaseClass = 'border-b-2 px-4 py-3 text-sm font-medium transition-colors'
const tabActiveClass = 'border-primary-500 text-primary-600'
const tabInactiveClass = 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'

const canOperate = computed(() => Boolean(selectedPlanId.value))

function handleSyncResult(sync) {
  state.applySyncResult(sync)
}

async function handleCustomerModalSaved(sync, payload) {
  await handleMonthCustomerSaved(sync, payload)
}

watch(selectedPlanId, async (planId) => {
  clearCompanyChainRelations()
  if (!planId) {
    customerWorkbenchRows.value = []
    customerWorkbenchTotal.value = 0
    customerWorkbenchPage.value = 1
    return
  }
  customerWorkbenchPage.value = 1
  await state.loadPlan(planId)
  await loadCustomerTagOptions()
  await loadCustomerWorkbench()
}, { immediate: false })

async function loadCustomerTagOptions() {
  try {
    const tags = await getEnterpriseInvoiceCustomerTagOptions()
    customerTags.value = Array.isArray(tags) ? tags : []
  } catch (error) {
    console.error('Failed to load enterprise invoice customer tag options', error)
    customerTags.value = []
  }
}

function customerWorkbenchParams() {
  const params = {
    page: customerWorkbenchPage.value,
    size: customerWorkbenchSize.value
  }
  const keyword = customerWorkbenchKeyword.value.trim()
  if (keyword) {
    params.keyword = keyword
  }
  if (customerWorkbenchJoinedFilter.value === 'joined') {
    params.joinedCurrentMonth = true
  }
  if (customerWorkbenchJoinedFilter.value === 'notJoined') {
    params.joinedCurrentMonth = false
  }
  if (customerWorkbenchPlannerUserId.value) {
    params.plannerUserId = customerWorkbenchPlannerUserId.value
  }
  if (customerWorkbenchFinanceUserId.value) {
    params.financeUserId = customerWorkbenchFinanceUserId.value
  }
  const channelSource = customerWorkbenchChannelSource.value.trim()
  if (channelSource) {
    params.channelSource = channelSource
  }
  return params
}

async function loadCustomerWorkbench() {
  if (!selectedPlanId.value) {
    customerWorkbenchRows.value = []
    customerWorkbenchTotal.value = 0
    return
  }
  customerWorkbenchLoading.value = true
  try {
    const page = await getEnterpriseInvoiceCustomerWorkbench(
      selectedPlanId.value,
      customerWorkbenchParams()
    )
    customerWorkbenchRows.value = page.records || []
    customerWorkbenchTotal.value = Number(page.total || 0)
    customerWorkbenchPage.value = Number(page.current || customerWorkbenchPage.value)
    customerWorkbenchSize.value = Number(page.size || customerWorkbenchSize.value)
  } catch (error) {
    console.error('Failed to load enterprise invoice customer workbench', error)
    ElMessage.error(error.message || '加载月度客户失败')
  } finally {
    customerWorkbenchLoading.value = false
  }
}

async function searchCustomerWorkbench() {
  customerWorkbenchPage.value = 1
  await loadCustomerWorkbench()
}

async function changeCustomerWorkbenchPage(page) {
  customerWorkbenchPage.value = page
  await loadCustomerWorkbench()
}

async function changeCustomerWorkbenchSize(size) {
  customerWorkbenchSize.value = size
  customerWorkbenchPage.value = 1
  await loadCustomerWorkbench()
}

async function handleMonthCustomerSaved(sync, payload) {
  if (!sync) return
  handleSyncResult(sync)
  const pendingTarget = payload?.regionBucketId
  const modalCustomerId = customerModal.customer?.customerId
  if (pendingTarget && modalCustomerId) {
    const matched = (sync.customers || []).find(customer => String(customer.customerId) === String(modalCustomerId))
    if (matched && String(matched.regionBucketId) !== String(pendingTarget)) {
      const moved = await state.runSyncAction(
        () => moveEnterpriseInvoiceCustomers(selectedPlanId.value, pendingTarget, {
          planCustomerIds: [matched.id],
          targetRegionId: pendingTarget
        }),
        '客户已添加到目标区域'
      )
      if (!moved) {
        ElMessage.warning('客户已加入本月，但移动到目标区域失败，可在名单中重新移动')
      }
    }
  }
  await loadCustomerWorkbench()
}

function statusClass(status) {
  const tone = statusTone(status)
  return badgeClassByTone[tone] || badgeClassByTone.neutral
}

function tabClass(tab) {
  return [tabBaseClass, activeTab.value === tab ? tabActiveClass : tabInactiveClass]
}

function ensurePlanSelected() {
  return canOperate.value
}

function openCreatePlan() {
  planModal.mode = 'create'
  planModal.basePlan = null
  planModal.visible = true
}

function openCopyPlan(plan) {
  planModal.mode = 'copy'
  planModal.basePlan = plan
  planModal.visible = true
}

async function afterPlanSaved(plan) {
  await planPanelRef.value?.loadPlans?.()
  selectedPlanId.value = plan?.id || selectedPlanId.value
}

async function reloadCurrentPlan() {
  if (!selectedPlanId.value) return
  await state.loadPlan(selectedPlanId.value)
  await loadCustomerTagOptions()
  await loadCustomerWorkbench()
}

function openJoinMonth(row) {
  customerModal.customer = {
    customerId: row.customerId,
    companyName: row.companyName,
    registeredRegion: row.registeredRegion,
    targetOutputAmount: null,
    targetProfitRate: null,
    regionBucketId: null,
    inputProductQuotas: row.inputProductQuotas,
    outputProductQuotas: row.outputProductQuotas,
    inputProductNames: row.inputProductNames,
    outputProductNames: row.outputProductNames
  }
  customerModal.visible = true
}

function openEditMonth(row) {
  customerModal.customer = {
    id: row.planCustomerId,
    customerId: row.customerId,
    companyName: row.companyNameSnapshot || row.companyName,
    registeredRegion: row.registeredRegionSnapshot || row.registeredRegion,
    regionBucketId: row.regionBucketId,
    targetOutputAmount: row.targetOutputAmount,
    targetProfitRate: row.targetProfitRate,
    inputProductQuotas: row.inputProductQuotas,
    outputProductQuotas: row.outputProductQuotas,
    inputProductNames: row.inputProductNames,
    outputProductNames: row.outputProductNames,
    displayOrder: row.displayOrder
  }
  customerModal.visible = true
}

function openCreateMonthFromRegion(payload) {
  const region = payload?.region || null
  customerModal.customer = {
    customerId: null,
    companyName: '',
    registeredRegion: region?.regionName || '',
    targetOutputAmount: null,
    targetProfitRate: null,
    regionBucketId: region?.id || null
  }
  customerModal.visible = true
}

function openEditMonthFromPlanCustomer(customer) {
  if (!customer?.id) return
  customerModal.customer = {
    id: customer.id,
    customerId: customer.customerId,
    companyName: customer.companyNameSnapshot || customer.companyName || customer.customerName || customer.name,
    registeredRegion: customer.registeredRegionSnapshot || customer.registeredRegion,
    regionBucketId: customer.regionBucketId,
    targetOutputAmount: customer.targetOutputAmount,
    targetProfitRate: customer.targetProfitRate,
    inputProductQuotas: customer.inputProductQuotas,
    outputProductQuotas: customer.outputProductQuotas,
    inputProductNames: customer.inputProductNames,
    outputProductNames: customer.outputProductNames,
    displayOrder: customer.displayOrder
  }
  customerModal.visible = true
}

async function removeFromMonth(row) {
  if (!ensurePlanSelected() || !row?.planCustomerId) return
  try {
    await ElMessageBox.confirm(
      '确认将该客户从本月移除？客户主档会保留。',
      '移除本月客户',
      {
        confirmButtonText: '移除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
  } catch {
    return
  }
  const sync = await state.runSyncAction(
    () => deleteEnterpriseInvoicePlanCustomer(selectedPlanId.value, row.planCustomerId),
    '已从本月移除'
  )
  if (sync) await loadCustomerWorkbench()
}

function openCreateMasterCustomer() {
  masterCustomerModal.mode = 'create'
  masterCustomerModal.customer = null
  masterCustomerModal.tagIds = []
  masterCustomerModal.visible = true
  loadCustomerTagOptions()
}

async function openEditMasterCustomer(row) {
  if (!row?.customerId) return
  masterCustomerModal.mode = 'edit'
  masterCustomerModal.customer = row
  masterCustomerModal.tagIds = []
  masterCustomerModal.visible = true
  await Promise.all([
    loadCustomerTagOptions(),
    loadCustomerTagsForModal(row.customerId)
  ])
}

async function saveMasterCustomer(payload) {
  if (!payload?.customer) return
  if (masterCustomerModal.saving) return
  masterCustomerModal.saving = true
  try {
    if (masterCustomerModal.mode === 'edit') {
      const updated = await updateEnterpriseInvoiceCustomer(masterCustomerModal.customer.customerId, payload.customer)
      await saveCustomerTags(updated?.id || masterCustomerModal.customer.customerId, payload.tagIds)
      ElMessage.success('客户已更新')
      masterCustomerModal.visible = false
      await loadCustomerWorkbench()
      return
    }

    if (payload.joinMonth) {
      if (!selectedPlanId.value) {
        ElMessage.warning('请选择月度方案')
        return
      }
      const sync = await createEnterpriseInvoiceCustomerWithMonth(selectedPlanId.value, payload)
      handleSyncResult(sync)
      ElMessage.success('客户已创建并加入本月')
      masterCustomerModal.visible = false
      await loadCustomerWorkbench()
      return
    }

    const created = await createEnterpriseInvoiceCustomer(payload.customer)
    await saveCustomerTags(created.id, payload.tagIds)
    ElMessage.success('客户已创建')
    masterCustomerModal.visible = false
    await loadCustomerWorkbench()
  } catch (error) {
    console.error('Failed to save enterprise invoice customer master', error)
    ElMessage.error(error.message || '保存客户失败')
  } finally {
    masterCustomerModal.saving = false
  }
}

async function loadCustomerTagsForModal(customerId) {
  masterCustomerModal.tagsLoading = true
  try {
    const tags = await getEnterpriseInvoiceCustomerTags(customerId)
    masterCustomerModal.tagIds = (Array.isArray(tags) ? tags : []).map(tag => tag.id)
  } catch (error) {
    console.error('Failed to load enterprise invoice customer tags', error)
    masterCustomerModal.tagIds = []
    ElMessage.warning('客户标签加载失败')
  } finally {
    masterCustomerModal.tagsLoading = false
  }
}

async function saveCustomerTags(customerId, tagIds = []) {
  if (!customerId) return
  try {
    await replaceEnterpriseInvoiceCustomerTags(customerId, { tagIds: tagIds || [] })
  } catch (error) {
    console.error('Failed to save enterprise invoice customer tags', error)
    ElMessage.warning('客户信息已保存，但标签保存失败，可重新编辑标签')
  }
}

async function openDownstreamAllocation(customer) {
  if (!customer?.id) return
  await loadCustomerTagOptions()
  downstreamAllocationDialog.customer = customer
  downstreamAllocationDialog.visible = true
}

async function openDownstreamTagPreference(customer) {
  if (!customer?.id) return
  await loadCustomerTagOptions()
  downstreamTagPreferenceDialog.customer = customer
  downstreamTagPreferenceDialog.visible = true
}

async function saveDownstreamAllocations(payload) {
  if (!ensurePlanSelected() || !payload?.customer?.id) return
  const sync = await state.runSyncAction(
    () => replaceEnterpriseInvoiceDownstreamAllocations(selectedPlanId.value, payload.customer.id, {
      allocations: payload.allocations || []
    }),
    '指定下游已保存'
  )
  if (sync) {
    downstreamAllocationDialog.visible = false
    await loadCustomerWorkbench()
  }
}

async function saveDownstreamTagPreferences(payload) {
  if (!ensurePlanSelected() || !payload?.customer?.id) return
  const sync = await state.runSyncAction(
    () => replaceEnterpriseInvoiceDownstreamTagPreferences(selectedPlanId.value, payload.customer.id, {
      preferences: payload.preferences || []
    }),
    '下游标签偏好已保存'
  )
  if (sync) {
    downstreamTagPreferenceDialog.visible = false
    await loadCustomerWorkbench()
  }
}

async function deleteMasterCustomer(row) {
  if (!row?.customerId) return
  try {
    await ElMessageBox.confirm(
      '确认删除该客户主档？存在任意月度开票记录时后端会拒绝删除。',
      '删除客户',
      {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    await deleteEnterpriseInvoiceCustomer(row.customerId)
    ElMessage.success('客户已删除')
    await loadCustomerWorkbench()
  } catch (error) {
    if (error === 'cancel') return
    console.error('Failed to delete enterprise invoice customer', error)
    ElMessage.error(error.message || '删除客户失败')
  }
}

function openCreateRegion() {
  regionModal.region = null
  regionModal.visible = true
}

function openRenameRegion(region) {
  regionModal.region = region
  regionModal.visible = true
}

async function saveRegionModal(payload) {
  if (!ensurePlanSelected()) return
  const action = payload.id
    ? () => updateEnterpriseInvoiceRegion(selectedPlanId.value, payload.id, { regionName: payload.regionName })
    : () => createEnterpriseInvoiceRegion(selectedPlanId.value, { regionName: payload.regionName })
  const sync = await state.runSyncAction(action, payload.id ? '区域已重命名' : '区域已创建')
  if (sync) {
    regionModal.visible = false
    await loadCustomerWorkbench()
  }
}

async function deleteRegion(region) {
  if (!ensurePlanSelected() || !region?.id) return
  const regionName = region.regionName || '未命名区域'
  try {
    await ElMessageBox.confirm(
      `确定删除区域“${regionName}”吗？删除后不可恢复。`,
      '删除区域',
      {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
  } catch {
    return
  }
  const sync = await state.runSyncAction(
    () => deleteEnterpriseInvoiceRegion(selectedPlanId.value, region.id),
    '区域已删除'
  )
  if (sync) await loadCustomerWorkbench()
}

async function reorderRegions(regions) {
  if (!ensurePlanSelected()) return
  const sync = await state.runSyncAction(
    () => reorderEnterpriseInvoiceRegions(selectedPlanId.value, { regionIds: regions.map(region => region.id) }),
    '区域顺序已保存'
  )
  if (sync) await loadCustomerWorkbench()
}

async function sortRegionsByAmount() {
  if (!ensurePlanSelected()) return
  const sync = await state.runSyncAction(
    () => sortEnterpriseInvoiceRegionsByAmount(selectedPlanId.value, true),
    '区域已按金额排序'
  )
  if (sync) await loadCustomerWorkbench()
}

async function moveMatrixCustomer(payload) {
  if (!ensurePlanSelected() || !payload?.targetRegionId || !payload?.planCustomerId) return null
  const planCustomerId = payload.planCustomerId
  const sync = await state.runSyncAction(
    () => moveEnterpriseInvoiceCustomers(selectedPlanId.value, payload.targetRegionId, {
      planCustomerIds: [planCustomerId],
      targetRegionId: payload.targetRegionId,
      beforePlanCustomerId: payload.beforePlanCustomerId,
      afterPlanCustomerId: payload.afterPlanCustomerId
    }),
    '客户区域已更新'
  )
  if (sync) payload.finishEdit?.()
  if (sync) await loadCustomerWorkbench()
  return sync
}

async function deleteCustomer(customer) {
  if (!ensurePlanSelected() || !customer?.id) return
  const customerName = customer.companyNameSnapshot || customer.companyName || customer.customerName || customer.name || '未命名客户'
  try {
    await ElMessageBox.confirm(
      `确定删除客户“${customerName}”吗？删除后不可恢复。`,
      '删除客户',
      {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
  } catch {
    return
  }
  const sync = await state.runSyncAction(
    () => deleteEnterpriseInvoicePlanCustomer(selectedPlanId.value, customer.id),
    '客户已从当月方案删除'
  )
  if (sync) await loadCustomerWorkbench()
}

async function confirmList() {
  if (!ensurePlanSelected()) return
  await state.runSyncAction(
    () => confirmEnterpriseInvoiceList(selectedPlanId.value),
    '名单已确认'
  )
}

async function generateChains() {
  if (!ensurePlanSelected()) return
  if (!canGenerateChains(state.plan.value)) return
  const sync = await state.runSyncAction(
    () => generateEnterpriseInvoiceChains(selectedPlanId.value, {
      minChainSubjectCount: state.plan.value?.minChainSubjectCount || 10,
      maxChainSubjectCount: state.plan.value?.maxChainSubjectCount || 20,
      startAmountTiers: state.plan.value?.startAmountTiers
    }),
    '链路已生成'
  )
  if (sync) {
    activeTab.value = 'chains'
  }
}

async function loadCompanyChainRelations(keyword) {
  if (!ensurePlanSelected() || companyRelationLoading.value) return
  const companyName = String(keyword ?? companyRelationKeyword.value ?? '').trim()
  if (!companyName) {
    ElMessage.warning('请输入公司名称')
    return
  }
  companyRelationKeyword.value = companyName
  companyRelationLoading.value = true
  companyRelationFocusChainId.value = null
  try {
    companyRelationResult.value = await getEnterpriseInvoiceCompanyChainRelations(selectedPlanId.value, companyName)
  } catch (error) {
    console.error('Failed to load enterprise invoice company chain relations', error)
    ElMessage.error(error.message || '查询公司上下游失败')
  } finally {
    companyRelationLoading.value = false
  }
}

function clearCompanyChainRelations() {
  companyRelationKeyword.value = ''
  companyRelationResult.value = null
  companyRelationLoading.value = false
  companyRelationFocusChainId.value = null
}

function locateCompanyRelation(match) {
  const chainId = match?.chain?.id
  if (!chainId) return
  companyRelationFocusChainId.value = chainId
  activeTab.value = 'chains'
}

function defaultExportFileName() {
  const month = String(state.plan.value?.planMonth || '').replace(/-/g, '')
  return `企营增${month || '开票链路'}.xlsx`
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName || '企营增开票链路.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

async function exportChains() {
  if (!ensurePlanSelected() || exportingChains.value) return
  exportingChains.value = true
  try {
    const blob = await exportEnterpriseInvoiceChains(selectedPlanId.value)
    downloadBlob(blob, defaultExportFileName())
    ElMessage.success('导出已开始')
  } catch (error) {
    console.error('Failed to export enterprise invoice chains', error)
    ElMessage.error(error.message || '导出链路失败')
  } finally {
    exportingChains.value = false
  }
}

async function regenerateUnlocked() {
  if (!ensurePlanSelected()) return
  await state.runSyncAction(
    () => regenerateEnterpriseInvoiceUnlockedChains(selectedPlanId.value),
    '未锁定链路已重排'
  )
}

async function generateUnfilled() {
  if (!ensurePlanSelected()) return
  await state.runSyncAction(
    () => generateEnterpriseInvoiceForUnfilledCustomers(selectedPlanId.value),
    '未完成客户已补齐'
  )
}

function openEditNode(payload) {
  nodePanel.context = payload
  nodePanel.visible = true
}

async function lockChain({ chain, locked }) {
  if (!ensurePlanSelected() || !chain?.id) return
  const action = locked
    ? () => lockEnterpriseInvoiceChain(selectedPlanId.value, chain.id)
    : () => unlockEnterpriseInvoiceChain(selectedPlanId.value, chain.id)
  await state.runSyncAction(action, locked ? '链路已锁定' : '链路已解锁')
}

async function lockNode(payload) {
  if (!ensurePlanSelected() || !payload?.chain?.id || !payload?.node?.id) return
  await state.runSyncAction(
    () => lockEnterpriseInvoiceChainNode(selectedPlanId.value, payload.chain.id, payload.node.id, payload.locks),
    '节点锁定状态已保存'
  )
}

async function regenerateChain(chain) {
  if (!ensurePlanSelected() || !chain?.id) return
  await state.runSyncAction(
    () => regenerateEnterpriseInvoiceChain(selectedPlanId.value, chain.id),
    '链路已重排'
  )
}

async function regenerateAfterNode({ chain, node }) {
  if (!ensurePlanSelected() || !chain?.id || !node?.id) return
  await state.runSyncAction(
    () => regenerateEnterpriseInvoiceAfterNode(selectedPlanId.value, chain.id, node.id),
    '节点后链路已重排'
  )
}

async function saveNodeEdit(payload) {
  if (!ensurePlanSelected() || !payload?.chain?.id || !payload?.node?.id) return
  const nodePayload = payload.nodePayload || payload.data || null
  let sync = null
  if (nodePayload) {
    sync = await state.runSyncAction(
      () => updateEnterpriseInvoiceChainNode(selectedPlanId.value, payload.chain.id, payload.node.id, nodePayload),
      '节点已保存'
    )
    if (!sync) return
  }
  if (payload.locks) {
    sync = await state.runSyncAction(
      () => lockEnterpriseInvoiceChainNode(selectedPlanId.value, payload.chain.id, payload.node.id, payload.locks),
      '节点锁定状态已保存'
    )
    if (!sync) return
  }
  if (!nodePayload && !payload.locks) return
  nodePanel.visible = false
}

async function validatePlan() {
  if (!ensurePlanSelected()) return
  await state.refreshIssues(selectedPlanId.value, '校验已完成')
}

async function confirmPlan() {
  if (!ensurePlanSelected()) return
  if (state.hasErrorIssues.value) {
    ElMessage.warning('存在 ERROR 校验问题，请处理后再确认方案')
    return
  }
  await state.runSyncAction(
    () => confirmEnterpriseInvoicePlan(selectedPlanId.value),
    '方案已确认'
  )
}

defineExpose({ validatePlan })
</script>
